import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { AdminGuard } from '@/components/guards/AdminGuard';
import { PageHeader } from '@/components/layout/PageHeader';
import { SettingsForm } from '@/components/admin/SettingsForm';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import { useToast } from '@/components/ui/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { settingsApi, settingsKeys } from '@/lib/api';
import type { UpdateSettingsRequest } from '@/types';

export const Route = createFileRoute('/admin/settings')({
  component: AdminSettingsPage,
});

function AdminSettingsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: settings,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: settingsKeys.detail(),
    queryFn: () => settingsApi.get(),
  });

  const { mutate: updateSettings, isPending: isUpdating } = useMutation({
    mutationFn: (data: UpdateSettingsRequest) => settingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: settingsKeys.all });
      toast({
        title: 'Settings Saved',
        description: 'Your settings have been updated successfully.',
      });
    },
    onError: (err: Error) => {
      toast({
        title: 'Save Failed',
        description: err.message || 'Failed to save settings.',
        variant: 'destructive',
      });
    },
  });

  return (
    <AdminGuard>
      <AppLayout>
        <div className="container max-w-4xl py-8">
          <PageHeader
            title="Settings"
            description="Configure global application settings"
          />

          {isLoading ? (
            <div className="flex items-center justify-center min-h-[400px]">
              <LoadingSpinner size="lg" label="Loading settings..." />
            </div>
          ) : isError ? (
            <ErrorMessage
              title="Failed to Load Settings"
              error={error}
              onRetry={() => refetch()}
            />
          ) : settings ? (
            <SettingsForm
              settings={settings}
              onSubmit={updateSettings}
              isSubmitting={isUpdating}
            />
          ) : null}

          <Toaster />
        </div>
      </AppLayout>
    </AdminGuard>
  );
}
